import { useCallback, useEffect, useRef, useState } from 'react'
import { Autocomplete, GoogleMap } from '@react-google-maps/api'
import { MapPin, Crosshair, Loader2 } from 'lucide-react'
import { useGoogleMaps } from './GoogleMapsProvider'

export interface LocationValue {
  address?: string
  lat?: number | null
  lng?: number | null
}

interface LocationPickerProps {
  value: LocationValue
  onChange: (value: LocationValue) => void
  label?: string
  placeholder?: string
  description?: string
  className?: string
  showUseMyLocation?: boolean
}
const mapOptionsBase: google.maps.MapOptions = {
  disableDefaultUI: true,
  zoomControl: true,
  streetViewControl: false,
}

const defaultCenter = { lat: 20, lng: 0 }

export function LocationPicker({
  value,
  onChange,
  label,
  placeholder,
  description,
  className,
  showUseMyLocation = true,
}: LocationPickerProps) {
  const { isLoaded, loadError } = useGoogleMaps()
  const autocompleteRef = useRef<google.maps.places.Autocomplete | null>(null)
  const mapRef = useRef<google.maps.Map | null>(null)
  const markerRef = useRef<google.maps.marker.AdvancedMarkerElement | null>(null)
  const [isLocating, setIsLocating] = useState(false)
  const hasCoordinates = typeof value.lat === 'number' && typeof value.lng === 'number'

  const [center, setCenter] = useState(() => {
    if (hasCoordinates) {
      return { lat: value.lat as number, lng: value.lng as number }
    }
    return defaultCenter
  })

  useEffect(() => {
    if (typeof value.lat === 'number' && typeof value.lng === 'number') {
      setCenter({ lat: value.lat, lng: value.lng })
    }
  }, [value.lat, value.lng])

  // Create/update advanced marker when position changes
  useEffect(() => {
    const map = mapRef.current
    if (!map || !isLoaded) return

    // Remove existing marker if any
    if (markerRef.current) {
      markerRef.current.map = null
      markerRef.current = null
    }

    // Create new marker if we have coordinates
    if (typeof value.lat === 'number' && typeof value.lng === 'number') {
      const createMarker = async () => {
        const { AdvancedMarkerElement } = await google.maps.importLibrary('marker') as google.maps.MarkerLibrary
        
        const marker = new AdvancedMarkerElement({
          map,
          position: { lat: value.lat as number, lng: value.lng as number },
          gmpDraggable: true,
        })

        marker.addListener('dragend', (event: google.maps.MapMouseEvent) => {
          if (!event.latLng) return
          const lat = event.latLng.lat()
          const lng = event.latLng.lng()
          setCenter({ lat, lng })
          onChange({
            ...value,
            lat,
            lng,
          })
        })

        markerRef.current = marker
      }

      createMarker()
    }

    return () => {
      if (markerRef.current) {
        markerRef.current.map = null
        markerRef.current = null
      }
    }
  }, [value.lat, value.lng, isLoaded, onChange, value])

  const handlePlaceChanged = useCallback(() => {
    const place = autocompleteRef.current?.getPlace()
    if (!place || !place.geometry?.location) return

    const lat = place.geometry.location.lat()
    const lng = place.geometry.location.lng()

    const address = place.formatted_address || place.name || value.address

    setCenter({ lat, lng })
    onChange({ address, lat, lng })

    // If map already loaded, pan/zoom to selection
    requestAnimationFrame(() => {
      const map = mapRef.current
      if (!map) return
      map.panTo({ lat, lng })
      if ((map.getZoom() || 0) < 14) {
        map.setZoom(14)
      }
    })
  }, [onChange, value.address])

  const handleMapClick = useCallback((event: google.maps.MapMouseEvent) => {
    if (!event.latLng) return
    const lat = event.latLng.lat()
    const lng = event.latLng.lng()
    setCenter({ lat, lng })
    onChange({
      ...value,
      lat,
      lng,
    })
  }, [onChange, value])

  const handleUseMyLocation = useCallback(() => {
    if (typeof navigator === 'undefined' || !navigator.geolocation) return
    setIsLocating(true)
    navigator.geolocation.getCurrentPosition(
      (position) => {
        const { latitude, longitude } = position.coords
        setCenter({ lat: latitude, lng: longitude })
        onChange({
          address: value.address,
          lat: latitude,
          lng: longitude,
        })
        const map = mapRef.current
        if (map) {
          map.panTo({ lat: latitude, lng: longitude })
          map.setZoom(Math.max(map.getZoom() || 0, 13))
        }
        setIsLocating(false)
      },
      () => setIsLocating(false),
      {
        enableHighAccuracy: true,
        maximumAge: 1000 * 60 * 5,
      }
    )
  }, [onChange, value.address])

  if (loadError) {
    return (
      <div className={`space-y-2 ${className || ''}`}>
        {label && (
          <label className="block text-sm font-medium text-gray-700">
            {label}
          </label>
        )}
        <div className="text-sm text-red-600 bg-red-50 border border-red-200 rounded p-3">
          Failed to load Google Maps. Please check your API key configuration.
        </div>
      </div>
    )
  }

  return (
    <div className={`space-y-2 ${className || ''}`}>
      {label && (
        <label className="block text-sm font-medium text-gray-700">
          {label}
        </label>
      )}

      <div className="relative">
        <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400 z-10 pointer-events-none" />
        {isLoaded ? (
          <Autocomplete
            onLoad={(autocomplete) => {
              autocompleteRef.current = autocomplete
            }}
            onPlaceChanged={handlePlaceChanged}
            fields={['formatted_address', 'geometry', 'name']}
          >
            <input
              type="text"
              value={value.address || ''}
              onChange={(e) => {
                onChange({ ...value, address: e.target.value })
              }}
              placeholder={placeholder || 'Search for a location'}
              className="input w-full pl-10"
            />
          </Autocomplete>
        ) : (
          <div className="input w-full pl-10 flex items-center gap-2 text-gray-500">
            <Loader2 className="h-4 w-4 animate-spin" />
            Loading map…
          </div>
        )}
      </div>

      {description && (
        <p className="text-xs text-gray-500">{description}</p>
      )}

      <div className="relative">
        {isLoaded ? (
          <GoogleMap
            center={center}
            zoom={hasCoordinates ? 13 : 2}
            onClick={handleMapClick}
            onLoad={(map) => {
              mapRef.current = map
              if (typeof value.lat === 'number' && typeof value.lng === 'number') {
                map.panTo({ lat: value.lat, lng: value.lng })
                map.setZoom(13)
              }
            }}
            mapContainerClassName="w-full rounded-lg border border-gray-200 overflow-hidden"
            mapContainerStyle={{ width: '100%', height: '320px' }}
            options={{
              ...mapOptionsBase,
              mapId: 'WAYBOOK_ADMIN_MAP', // Required for AdvancedMarkerElement
            }}
          >
            {/* Marker is now managed via useEffect hook above */}
          </GoogleMap>
        ) : (
          <div className="w-full h-80 bg-gray-100 border border-dashed border-gray-200 rounded-lg flex items-center justify-center text-gray-500 text-sm">
            Loading map…
          </div>
        )}

        {showUseMyLocation && (
          <button
            type="button"
            onClick={handleUseMyLocation}
            className="absolute right-3 top-3 bg-white border border-gray-200 shadow-sm rounded-full px-3 py-1.5 text-xs font-medium text-gray-700 hover:bg-gray-50 flex items-center gap-2"
            disabled={isLocating}
          >
            {isLocating ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Crosshair className="h-4 w-4" />
            )}
            Use my location
          </button>
        )}
      </div>
    </div>
  )
}
